import { createContext, useCallback, useState, useEffect, useMemo } from 'react'

import AuthService from 'modules/auth/service'
import { queryClient } from 'core/config/request'
import Loading from 'common/components/loading'
import { User } from '../types'

type AuthContextProps = {
  isLogged: () => boolean,
  changeToPublicAccess: () => void,
  logout: () => void
}

export const AuthContext = createContext<AuthContextProps>({} as AuthContextProps)

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isCheckingLogin, setIsCheckingLogin] = useState(true)
  const [user, setUser] = useState<User | null>(null)

  const onSubscribe = useCallback((userCas: User) => {
    setUser(userCas)
    setIsCheckingLogin(false)
  }, [])

  useEffect(() => {
    if (isCheckingLogin) {
      AuthService.subscribe({
        next: onSubscribe,
        error: () => {
          setIsCheckingLogin(false)
        }
      })
      queryClient.resetQueries()
      AuthService.start()
    }
  }, [isCheckingLogin, onSubscribe])

  const changeToPublicAccess = useCallback(() => {
    AuthService.stop()
  }, [])

  const logout = useCallback(() => {
    AuthService.logout()
  }, [])

  const isLogged = useCallback(() => !!user?.id, [user])

  const contextValue = useMemo(() => ({
    isLogged,
    changeToPublicAccess,
    logout
  }), [isLogged, changeToPublicAccess, logout])

  if (isCheckingLogin) {
    return <Loading full />
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}
